// src/features/retour-absence/etat.ts — SCE Issues
// Le retour d'absence en cours (28/10), partagé entre l'accueil qui le lit et
// le menu du compte qui propose « Le récap de votre absence » sur toutes les
// pages pendant DUREE_RECAP_JOURS. Petit magasin en mémoire : le bloc vivant
// (étapes cochées, vu) et la demande d'ouverture venue du menu.

import { useSyncExternalStore } from 'react'
import type { BlocRetourAbsence } from '@/utils/retourAbsence'

export interface EtatRetour {
  upn: string
  bloc: BlocRetourAbsence | null
  /** Le récap a du contenu et n'est pas terminé : le menu le propose. */
  disponible: boolean
  /** Le menu a demandé de l'ouvrir ; l'accueil la consomme. */
  ouvertureDemandee: boolean
  /**
   * Parti du récap vers une fiche ou une page : il reviendra sur cette carte
   * quand on revient (la fiche refermée, ou l'accueil remonté au téléphone).
   * `par` : l'accueil qui l'a noté ; `t` : quand.
   */
  reprise: { carte: number; par: number; t: number } | null
}

let etat: EtatRetour = { upn: '', bloc: null, disponible: false, ouvertureDemandee: false, reprise: null }
const abonnes = new Set<() => void>()

function changer(partiel: Partial<EtatRetour>): void {
  etat = { ...etat, ...partiel }
  for (const f of abonnes) f()
}

/**
 * Le bloc lu au passage sur l'accueil. Un bloc déjà connu pour le même retour
 * est gardé : il est plus frais (vu, étapes cochées) que la lecture partagée
 * par deux montages rapprochés (useAccueil).
 */
export function poserRetour(upn: string, bloc: BlocRetourAbsence | null): void {
  if (etat.upn === upn && etat.bloc && bloc && etat.bloc.retour === bloc.retour) return
  changer({ upn, bloc, ...(etat.upn !== upn ? { disponible: false } : {}) })
}

/** Le bloc après un changement fait ici (écrit ou non selon la bascule de rôle). */
export function remplacerBloc(upn: string, bloc: BlocRetourAbsence | null): void {
  if (etat.upn !== upn) return
  changer({ bloc })
}

export function annoncerDisponible(disponible: boolean): void {
  if (etat.disponible !== disponible) changer({ disponible })
}

export function demanderOuverture(): void {
  changer({ ouvertureDemandee: true })
}

export function oublierDemande(): void {
  if (etat.ouvertureDemandee) changer({ ouvertureDemandee: false })
}

export function noterReprise(carte: number, par: number): void {
  changer({ reprise: { carte, par, t: Date.now() } })
}

export function oublierReprise(): void {
  if (etat.reprise) changer({ reprise: null })
}

function abonner(f: () => void): () => void {
  abonnes.add(f)
  return () => abonnes.delete(f)
}

export function useEtatRetour(): EtatRetour {
  return useSyncExternalStore(abonner, () => etat, () => etat)
}
